import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { BeforeAfterSlider } from '../components/BeforeAfterSlider';
import { 
  ArrowRight, ChevronLeft, ChevronRight, X, Star, Clock, PoundSterling
} from 'lucide-react';

// Gallery data - Before/After transformations with realistic image pairs
const galleryProjects = [
  {
    id: 1,
    title: 'Living Room Repaint',
    category: 'Decorating',
    description: 'Full living room repaint including walls, ceiling, and all woodwork. Transformed from dated pink walls to modern neutral grey tones.',
    beforeImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/10116bf656e4189e6e4df85ad3a55c523701282e2584ba7dbc62d47700ea476c.png',
    afterImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/615395dec0034f78f0cd8d403ea454e4c9f8ae38b60ea17522ec7e465d48aab8.png',
    duration: '2 days',
    savings: '£180',
    rating: 5
  },
  {
    id: 2,
    title: 'Internal Door Fitting',
    category: 'Carpentry',
    description: 'Replaced old damaged door with new white panel door. Fitted modern chrome handle and painted frame for a fresh, clean look.',
    beforeImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/1d8acf164ae32990cfa720dde5c94e146066ba75a5535192bfe92a076586796d.png',
    afterImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/d94a22934c999dd2f9441d683a816cf7eae6eaaa1073b97c1275c91013da95fa.png',
    duration: '1 day',
    savings: '£200',
    rating: 5
  },
  {
    id: 3,
    title: 'Garden Fence Replacement',
    category: 'Outdoors',
    description: 'Replaced broken and rotting fence panels with brand new treated timber fencing. Sturdy posts and neat finish throughout.',
    beforeImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/ab1638477d3d6825193f2c3964de0c7d3ca729b313175e602efd83593c4dc4de.png',
    afterImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/e2e17a6d0e57decc2141097b35a69a31ac9981d0fb5e687eddbf08c1153f78e2.png',
    duration: '1 day',
    savings: '£150',
    rating: 5
  },
  {
    id: 4,
    title: 'Bathroom Refresh',
    category: 'Plumbing',
    description: 'Re-sealed bath, replaced old taps with new chrome fittings, re-grouted tiles and gave the whole bathroom a fresh, clean finish.',
    beforeImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/8fc306cc627d3b75b27d93f24d4b9cfe9d22d49a6a7b6b873f1b9da9f52ab427.png',
    afterImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/4e4215c47316395e04f84de22e33dd68f6ade2e8def41965d35b4a6bf022d221.png',
    duration: 'Half day',
    savings: '£90',
    rating: 5
  },
  {
    id: 5,
    title: 'Home Office Setup',
    category: 'General',
    description: 'Transformed messy spare room into professional home office. Wall-mounted monitors, cable management, and floating shelves installed.',
    beforeImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/3e6bd93c040c667a6387a0abb6ab88d7e42112e13a2c269d4148723d6940d072.png',
    afterImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/6d7e97b64734fbaca1f69b6349bdf342787cc783ccf59fc10f0713018d5a2adb.png',
    duration: 'Half day',
    savings: '£75',
    rating: 5
  },
  {
    id: 6,
    title: 'Hallway & Stairs',
    category: 'Decorating',
    description: 'Complete hallway transformation - stripped old wallpaper, repaired walls, fresh paint throughout, and refinished the wooden banister.',
    beforeImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/d9648735acb14cf80baa29ca32db14fe29f7d5aa6c7b22343d27adc885f86866.png',
    afterImage: 'https://static.prod-images.emergentagent.com/jobs/a7b7b22f-6979-40cb-b834-a24a0cd38abc/images/7a568c2fc14ea937bd220ec774951a26d313cb0db7595b697bedf659eb32286e.png',
    duration: '3 days',
    savings: '£240',
    rating: 5
  }
];

const categories = ['All', 'Decorating', 'Carpentry', 'Outdoors', 'Plumbing', 'General'];

export const Gallery = () => {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedProject, setSelectedProject] = useState(null);
  const [showAfter, setShowAfter] = useState(true);

  const filteredProjects = selectedCategory === 'All' 
    ? galleryProjects 
    : galleryProjects.filter(p => p.category === selectedCategory);

  return (
    <div className="animate-in">
      <SEO 
        title="Our Work - Before & After Gallery"
        description="See real before and after photos from Expert-Handyman projects. Decorating, carpentry, plumbing transformations in Manchester & Cheshire."
        keywords="handyman before after, home transformation Manchester, decorating results, carpentry portfolio, property makeover"
      />
      
      {/* Hero */}
      <section className="bg-gradient-to-br from-primary to-primary/80 text-white py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 text-white/90 text-sm font-medium mb-6">
            Our Work
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 font-['Outfit'] tracking-tight">
            Before & After Gallery
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto">
            See the transformations we create. Real projects, real results, real savings.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 bg-muted border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-2 justify-center">
            {categories.map(cat => (
              <Button
                key={cat}
                variant={selectedCategory === cat ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat)}
                className="rounded-full"
              >
                {cat}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map(project => (
              <Card 
                key={project.id} 
                className="overflow-hidden cursor-pointer group hover:shadow-xl transition-all"
                onClick={() => { setSelectedProject(project); setShowAfter(true); }}
                data-testid="gallery-card"
              >
                <div className="relative">
                  <BeforeAfterSlider 
                    beforeImage={project.beforeImage}
                    afterImage={project.afterImage}
                    title={project.title}
                  />
                  <Badge className="absolute top-3 left-16 z-10 bg-primary">{project.category}</Badge>
                </div>
                <CardContent className="p-4">
                  <h3 className="font-semibold text-lg">{project.title}</h3>
                  <div className="flex items-center gap-3 mt-2 text-sm text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {project.duration}
                    </span>
                    <span className="flex items-center gap-1 text-green-600 font-medium">
                      <PoundSterling className="w-3 h-3" /> Saved {project.savings}
                    </span>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="p-0 max-h-[85vh] overflow-hidden">
          {selectedProject && (
            <div className="flex flex-col max-h-[85vh] overflow-y-auto">
              {/* Image with Before/After Toggle */}
              <div className="relative w-full h-40 sm:h-48 md:h-56 bg-muted flex-shrink-0">
                <img 
                  src={showAfter ? selectedProject.afterImage : selectedProject.beforeImage}
                  alt={selectedProject.title}
                  className="w-full h-full object-cover"
                />
                
                {/* Before/After Toggle */}
                <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex bg-black/50 backdrop-blur-sm rounded-full p-1">
                  <button 
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${!showAfter ? 'bg-white text-black' : 'text-white'}`}
                    onClick={() => setShowAfter(false)}
                  >
                    Before
                  </button>
                  <button 
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all ${showAfter ? 'bg-white text-black' : 'text-white'}`}
                    onClick={() => setShowAfter(true)}
                  >
                    After
                  </button>
                </div>
              </div>

              {/* Project Info */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <Badge className="mb-2">{selectedProject.category}</Badge>
                    <h2 className="text-xl sm:text-2xl font-bold font-['Outfit']">{selectedProject.title}</h2>
                  </div>
                  <div className="flex items-center gap-1 text-yellow-500">
                    {[...Array(selectedProject.rating)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                </div>

                <p className="text-muted-foreground text-sm sm:text-base mb-4">{selectedProject.description}</p>

                <div className="flex flex-wrap gap-2 sm:gap-4 mb-6">
                  <div className="flex items-center gap-2 px-3 py-2 bg-muted rounded-lg">
                    <Clock className="w-4 h-4 text-primary" />
                    <span className="text-xs sm:text-sm font-medium">{selectedProject.duration}</span>
                  </div>
                  <div className="flex items-center gap-2 px-3 py-2 bg-green-50 rounded-lg">
                    <PoundSterling className="w-4 h-4 text-green-600" />
                    <span className="text-xs sm:text-sm font-medium text-green-700">Saved {selectedProject.savings}</span>
                  </div>
                </div>

                <Button className="w-full btn-accent" onClick={() => navigate('/quote')}>
                  Get a Quote for Your Project <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-green-600 to-emerald-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4 font-['Outfit']">
            Ready for Your Transformation?
          </h2>
          <p className="text-white/80 mb-6 max-w-xl mx-auto">
            Get a free, no-obligation quote and see how much you can save on your next project.
          </p>
          <Button 
            size="lg"
            className="btn-accent"
            onClick={() => navigate('/quote')}
          >
            Get Free Quote <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
};
